import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Headphones } from 'lucide-react'
import { formatTime as formatLocaleTime, formatNumber } from '../../i18n/useLocaleFormat'
import Modal from './Modal'
import { useCostSummary, useProject, useSparks } from '../../api/hooks'
import type { NodeUsageItem } from '../../api/client'
import { EXECUTION_ACTION_LABELS } from '../../generated/nodeDataTypes'
import { createNadDebug } from '../../utils/debugLog'
import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { isPostpaidTeam } from '../../features/billing/teamBillingMode'
import PostpaidUsageSummary from '../../features/billing/PostpaidUsageSummary'

const audioUsageLog = createNadDebug('dw:audio-usage-count')

interface UsageCostModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string | undefined
}

interface NodeGroup {
  nodeId: string
  nodeType: string
  runs: NodeUsageItem[]
  totalCredits: number
  totalImages: number
  totalVideos: number
  totalAudio: number
  totalDurationMs: number | null
}

function groupNodesByNodeId(nodes: NodeUsageItem[]): NodeGroup[] {
  const groups = new Map<string, NodeUsageItem[]>()
  for (const node of nodes) {
    const existing = groups.get(node.node_id)
    if (existing) {
      existing.push(node)
    } else {
      groups.set(node.node_id, [node])
    }
  }

  return Array.from(groups.entries()).map(([nodeId, runs]) => ({
    nodeId,
    nodeType: runs[0].node_type,
    runs,
    totalCredits: runs.reduce((sum, n) => sum + n.charged_sparks, 0),
    totalImages: runs.reduce((sum, n) => sum + n.images_generated, 0),
    totalVideos: runs.reduce((sum, n) => sum + n.videos_generated, 0),
    totalAudio: runs.reduce((sum, n) => sum + (n.audio_generated ?? 0), 0),
    totalDurationMs: runs.some((n) => n.duration_ms != null)
      ? runs.reduce((sum, n) => sum + (n.duration_ms ?? 0), 0)
      : null,
  }))
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remaining = Math.round(seconds % 60)
  return `${minutes}m ${remaining}s`
}

function formatTime(iso: string | null): string {
  return formatLocaleTime(iso, { hour: '2-digit', minute: '2-digit' })
}

// Read-only credit balance row. Self-serve minting was removed (T276405916):
// credits are added by an operator via /admin/topup, not from this modal. This
// keeps the balance visible to everyone without any mint affordance.
//
// TAKES THE PROJECT, for the same reason the header badge does (RFC-1372 C2).
// `resolve_spark_wallet` derives the paying wallet FROM THE PROJECT; omit it and
// leg 1 is skipped entirely and the endpoint answers with the ACTOR'S OWN wallet
// — their personal team, or their `user_spark_balances` row. On a project owned
// by a real team those are different wallets holding different money, so this
// modal rendered a balance that no generation in this project will ever spend.
// Reported on dev-meta: the header badge read 4,958 (the team pool being
// charged) while this row read 60,366 (the viewer's own credits).
//
// This is the same defect b68ca2ae1 fixed for `SparksBalance`; that change
// threaded `projectId` through the badge and `InsufficientSparksModal` and
// missed this call site.
function BalanceRow({ projectId }: { projectId: string | undefined }) {
  const { t } = useTranslation('shared')
  // `balance` is `spendable` = min(pool, allowance), NOT the raw pool the header
  // badge shows. Deliberate, and the two can still differ for a member with a
  // per-member limit: the badge is a persistent chrome element answering "what
  // does the team hold", while this modal sits next to this user's own spend and
  // should answer "what can I spend". Over-promising here is what gets a run
  // refused after the number said it was affordable.
  const { data: sparksData, isLoading: sparksLoading } = useSparks(projectId)

  return (
    <div
      className="flex items-center gap-3 p-3 bg-surface-1 border border-white/10 rounded-lg"
      data-testid="modal-balance-row"
    >
      <div className="flex items-center gap-1.5 shrink-0">
        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
        </svg>
        <span className="text-sm font-medium text-foreground" data-testid="modal-sparks-balance">
          {sparksLoading ? '...' : formatNumber(sparksData?.balance ?? 0)}
        </span>
        <span className="text-xs font-medium text-foreground/50">{t('usageCostModal.credits')}</span>
      </div>
    </div>
  )
}

function StatCard({
  icon,
  value,
  label,
  testId,
}: {
  icon: React.ReactNode
  value: string | number
  label: string
  testId?: string
}) {
  const display = typeof value === 'number' ? formatNumber(value) : value
  return (
    <div
      className="min-w-0 flex flex-col items-center gap-1 px-3 py-3 bg-surface-1 border border-border rounded-md"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-2xl font-bold text-foreground">{display}</span>
      </div>
      <span className="text-xs font-medium text-foreground/50">{label}</span>
    </div>
  )
}

function MediaCell({ images, videos, audio }: { images: number; videos: number; audio: number }) {
  const { t } = useTranslation('shared')
  return (
    <span className="w-14 text-right">
      {images > 0 && (
        <span className="flex items-center gap-1 justify-end" title={t('usageCostModal.imagesTitle', { num: images })}>
          <svg className="w-3.5 h-3.5 text-blue-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span>{images}</span>
        </span>
      )}
      {videos > 0 && (
        <span className="flex items-center gap-1 justify-end" title={t('usageCostModal.videosTitle', { num: videos })}>
          <svg className="w-3.5 h-3.5 text-purple-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          <span>{videos}</span>
        </span>
      )}
      {audio > 0 && (
        <span className="flex items-center gap-1 justify-end" title={t('usageCostModal.audioTitle', { num: audio })}>
          <Headphones className="w-3.5 h-3.5 text-primary/70" strokeWidth={1.8} />
          <span>{audio}</span>
        </span>
      )}
      {images === 0 && videos === 0 && audio === 0 && <span>—</span>}
    </span>
  )
}

function NodeRow({ node, indented, runLabel }: { node: NodeUsageItem; indented?: boolean; runLabel?: string }) {
  const { t } = useTranslation('shared')
  const typeLabel = t(`usageCostModal.nodeType.${node.node_type}`, { defaultValue: node.node_type })
  const displayLabel = runLabel ?? (node.node_label ? `${typeLabel} — ${node.node_label}` : typeLabel)
  const failed = node.status === 'FAILED'

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors ${failed ? 'opacity-45' : ''} ${indented ? 'pl-8 bg-white/[0.01]' : ''}`}
      data-testid="node-breakdown-row"
      data-node-id={node.node_id}
      data-node-type={node.node_type}
      data-action={node.action}
      data-cost-category={node.cost_category}
      data-charged-sparks={node.charged_sparks}
      data-audio-generated={node.audio_generated ?? 0}
    >
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className={`text-sm truncate ${indented ? 'text-foreground/60' : 'text-foreground'}`}>
          {displayLabel}
        </span>
        {indented && node.started_at && (
          <span className="text-xxs text-foreground/30 tabular-nums shrink-0">
            {formatTime(node.started_at)}
          </span>
        )}
        {failed && (
          <span className="shrink-0 px-1.5 py-0.5 text-xxs font-medium bg-red-500/20 text-red-300 rounded">
            {t('usageCostModal.failed')}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4 text-xs font-medium text-foreground/50 shrink-0">
        <span className="flex items-center gap-1 w-14 justify-end" title={t('usageCostModal.creditsChargedTitle', { amount: node.charged_sparks })}>
          <svg className="w-3.5 h-3.5 text-yellow-300/70" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          <span className="text-foreground/70 font-medium">{node.charged_sparks}</span>
        </span>
        <MediaCell
          images={node.images_generated}
          videos={node.videos_generated}
          audio={node.audio_generated ?? 0}
        />
        <span className="w-16 text-right tabular-nums">{formatDuration(node.duration_ms)}</span>
      </div>
    </div>
  )
}

function GroupRow({ group }: { group: NodeGroup }) {
  const { t } = useTranslation('shared')
  const [expanded, setExpanded] = useState(false)
  const isSingle = group.runs.length === 1
  const typeLabel = t(`usageCostModal.nodeType.${group.nodeType}`, { defaultValue: group.nodeType })

  if (isSingle) {
    return <NodeRow node={group.runs[0]} />
  }

  return (
    <div data-testid="node-group" data-node-type={group.nodeType}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors cursor-pointer text-left"
        data-testid="node-group-toggle"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <svg
            className={`w-3.5 h-3.5 text-foreground/30 transition-transform shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm text-foreground truncate">
            {group.runs[0].node_label ? `${typeLabel} — ${group.runs[0].node_label}` : typeLabel}
          </span>
          <span className="shrink-0 px-1.5 py-0.5 text-xxs font-medium bg-white/10 text-foreground/50 rounded">
            {t('usageCostModal.runCount', { num: group.runs.length })}
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs font-medium text-foreground/50 shrink-0">
          <span className="flex items-center gap-1 w-14 justify-end" title={t('usageCostModal.creditsTotalTitle', { amount: group.totalCredits })}>
            <svg className="w-3.5 h-3.5 text-yellow-300/70" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
            </svg>
            <span className="text-foreground/70 font-medium">{group.totalCredits}</span>
          </span>
          <MediaCell
            images={group.totalImages}
            videos={group.totalVideos}
            audio={group.totalAudio}
          />
          <span className="w-16 text-right tabular-nums">{formatDuration(group.totalDurationMs)}</span>
        </div>
      </button>
      {expanded && (
        <div className="divide-y divide-white/[0.04]">
          {group.runs.map((node, idx) => (
            <NodeRow
              key={`${node.node_id}-${idx}`}
              node={node}
              indented
              runLabel={
                t(`usageCostModal.action.${node.node_type}.${node.action}`, {
                  defaultValue:
                    EXECUTION_ACTION_LABELS[node.node_type]?.[node.action]
                    ?? t('usageCostModal.runLabel', { index: idx + 1 }),
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function UsageCostModal(props: UsageCostModalProps) {
  const { t } = useTranslation('shared')
  const project = useProject(props.projectId ?? '')
  const { teams, isLoading: teamsLoading } = useActiveTeam()
  const billingTeam = teams.find((team) => team.id === project.data?.owner_team_id) ?? null
  if (
    props.isOpen &&
    project.data?.can_view_credits === true &&
    project.data.owner_team_id &&
    (teamsLoading || billingTeam === null)
  ) {
    return (
      <Modal isOpen={props.isOpen} onClose={props.onClose} title={t('usageCostModal.usageTitle')} size="md">
        <p role="status" className="text-base text-muted-foreground">{t('usageCostModal.loadingUsageData')}</p>
      </Modal>
    )
  }
  if (project.data?.can_view_credits === true && billingTeam && isPostpaidTeam(billingTeam)) {
    return (
      <Modal isOpen={props.isOpen} onClose={props.onClose} title={t('usageCostModal.usageTitle')} size="md">
        <PostpaidUsageSummary team={billingTeam} projectId={props.projectId} />
      </Modal>
    )
  }
  return <PrepaidUsageCostModal {...props} />
}

function PrepaidUsageCostModal({ isOpen, onClose, projectId }: UsageCostModalProps) {
  const { t } = useTranslation('shared')
  const project = useProject(projectId ?? '')
  const canViewCredits = project.data?.can_view_credits === true
  const { data, isLoading, error } = useCostSummary(
    isOpen && canViewCredits ? projectId : undefined,
  )

  const isEmpty = !isLoading && !error && (!data || data.node_count === 0)

  const groups = useMemo(
    () => (data ? groupNodesByNodeId(data.nodes) : []),
    [data],
  )

  useEffect(() => {
    if (!isOpen || !data) return
    const traceId = `audio-usage-count:${projectId ?? 'unknown-project'}`
    audioUsageLog({
      event: 'audio-usage-count:data-received',
      trace_id: traceId,
      nodeCount: data.node_count,
      totalCharged: data.total_sparks_charged,
      totalAudio: data.total_audio ?? 0,
    })
    if (data.node_count > 0) {
      // The event name states the invariant so the compact `nad trace` view is
      // readable on its own: the "audio credits" stat card is gone, and audio
      // spend is already folded into the single "credits" total.
      audioUsageLog({
        event: 'audio-usage-count:rendered-without-audio-credits-card',
        trace_id: traceId,
        statCardsRendered: 'credits,images,videos,audio',
        audioCreditsStatRendered: false,
        totalCharged: data.total_sparks_charged,
        totalAudioCharged: data.total_audio_sparks_charged,
        totalAudio: data.total_audio ?? 0,
      })
    }
  }, [data, isOpen, projectId])

  if (!canViewCredits) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('usageCostModal.title')} size="md">
      <div className="mb-5">
        <BalanceRow projectId={projectId} />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          <span className="ml-3 text-text-secondary">{t('usageCostModal.loadingUsageData')}</span>
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
          {t('usageCostModal.failedToLoadUsageData')}
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-12 h-12 text-foreground/15 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
            <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-text-muted text-sm">{t('usageCostModal.noUsageDataYet')}</p>
          <p className="text-foreground/25 text-xs font-medium mt-1">{t('usageCostModal.runWorkflowHint')}</p>
        </div>
      )}

      {data && data.node_count > 0 && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              icon={
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
                </svg>
              }
              value={data.total_sparks_charged}
              label={t('usageCostModal.credits')}
              testId="total-credits-stat"
            />
            <StatCard
              icon={
                <svg className="w-5 h-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              }
              value={data.total_images}
              label={t('usageCostModal.images')}
            />
            <StatCard
              icon={
                <svg className="w-5 h-5 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              }
              value={data.total_videos}
              label={t('usageCostModal.videos')}
            />
            <StatCard
              icon={<Headphones className="w-5 h-5 text-primary" strokeWidth={2} />}
              value={data.total_audio ?? 0}
              label={t('usageCostModal.audio')}
              testId="audio-count-stat"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/30 uppercase tracking-wider">{t('usageCostModal.executionBreakdown')}</span>
              <span className="text-xs font-medium text-foreground/30">{t('usageCostModal.runCount', { num: data.node_count })}</span>
            </div>
            <div className="border border-white/[0.07] rounded-md overflow-hidden">
              <div className="flex items-center gap-3 px-3 py-2 bg-white/[0.03] border-b border-white/[0.07] text-xxs text-foreground/30 uppercase tracking-wider">
                <div className="flex-1">{t('usageCostModal.step')}</div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="w-14 text-right">{t('usageCostModal.creditsHeader')}</span>
                  <span className="w-14 text-right">{t('usageCostModal.media')}</span>
                  <span className="w-16 text-right">{t('usageCostModal.duration')}</span>
                </div>
              </div>
              <div
                className="max-h-[280px] overflow-y-auto divide-y divide-white/[0.04]"
                data-testid="node-breakdown-rows"
                data-node-count={data.node_count}
              >
                {groups.map((group) => (
                  <GroupRow key={group.nodeId} group={group} />
                ))}
              </div>
            </div>
          </div>

          <div className="text-center text-xs font-medium text-foreground/30 pt-1">
            <div>{t('usageCostModal.chargedSummary', { charged: data.total_sparks_charged })}</div>
          </div>
        </div>
      )}
    </Modal>
  )
}
